/**
 * API utility for making requests to the backend
 *
 * Auth strategy:
 *  1. When embedded in Shopify Admin, App Bridge provides session tokens
 *     via `window.shopify.idToken()` — sent as Bearer on every request.
 *  2. When running standalone (dev/preview), falls back to a stored token
 *     (or none, when the server allows mock auth).
 */

// Use empty string for relative URLs - same origin in production,
// Vite proxy handles it in dev
const API_BASE = '';

class ApiClient {
  constructor(baseUrl = '') {
    this.baseUrl = baseUrl;
    this.token = localStorage.getItem('prolabel_token');
    this._sessionTokenCache = null;
    this._sessionTokenExpiry = 0;
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('prolabel_token', token);
    } else {
      localStorage.removeItem('prolabel_token');
    }
  }

  /**
   * Get the best available auth token:
   * App Bridge session token (embedded) > stored legacy token.
   */
  async getAuthToken() {
    // Try App Bridge (embedded)
    if (typeof window !== 'undefined' && window.shopify && typeof window.shopify.idToken === 'function') {
      try {
        const now = Math.floor(Date.now() / 1000);
        // Session tokens live ~60s; refresh when within 10s of expiry
        if (this._sessionTokenCache && this._sessionTokenExpiry - 10 > now) {
          return this._sessionTokenCache;
        }
        const token = await window.shopify.idToken();
        if (token) {
          this._sessionTokenCache = token;
          // Decode exp (no verification — display only)
          try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            this._sessionTokenExpiry = payload.exp || now + 50;
          } catch (e) {
            this._sessionTokenExpiry = now + 50;
          }
          return token;
        }
      } catch (e) {
        // Not embedded or not ready yet — fall through
      }
    }

    // Standalone fallback
    return this.token;
  }

  async request(method, path, body = null) {
    const headers = {
      'Content-Type': 'application/json'
    };

    const token = await this.getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const config = {
      method,
      headers
    };

    if (body && method !== 'GET') {
      config.body = JSON.stringify(body);
    }

    const response = await fetch(`${this.baseUrl}${path}`, config);

    // Handle PDF responses
    if (response.headers.get('content-type')?.includes('application/pdf')) {
      return response.blob();
    }

    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.error?.message || data.error || data.message || 'Request failed');
      error.status = response.status;
      error.code = data.error?.code;
      throw error;
    }

    return data;
  }

  get(path) {
    return this.request('GET', path);
  }

  post(path, body) {
    return this.request('POST', path, body);
  }

  put(path, body) {
    return this.request('PUT', path, body);
  }

  delete(path) {
    return this.request('DELETE', path);
  }
}

export const api = new ApiClient(API_BASE);
